/**
 * VehicleSummaryTab - Aba de resumo do modal premium de veículo
 *
 * Espelha o TaskSummaryTab: grid de cards de métrica + seção "Andamentos"
 * com a timeline completa (criação, edição, anexos e movimentações).
 *
 * @version 2.0.0
 * RELEASE: 2026-08-08 v2.0.0 - Andamentos completos no Resumo (cópia do modal de tarefas)
 *   - Remoção do feed somente leitura "Andamentos Recentes"
 *   - Uso do novo VehicleActivityTimeline
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial (padronização com CustomerSummaryTab)
 */

import React from 'react';
import { Briefcase, FileSignature, ListChecks, ReceiptText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { VehicleActivityTimeline } from './VehicleActivityTimeline';
import type { VehicleDetailsVehicle } from '@/hooks/useVehicleDetails';

interface VehicleSummaryTabProps {
  vehicle: VehicleDetailsVehicle;
  counts?: {
    deals?: number | null;
    contracts?: number | null;
    tasks?: number | null;
    fines?: number | null;
    notes?: number | null;
  } | null;
  /** Lazy loading dos andamentos */
  isActive?: boolean;
  /**
   * v2.1.0 (mobile): ficha lateral injetada no topo do Resumo quando o modal
   * está em tela cheia no celular (mesmo padrão do DealSummaryTab).
   */
  sidebarContent?: React.ReactNode;
}

/** Formata valores monetários em BRL */
const formatCurrency = (value: number | null) =>
  value == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/** Card de métrica compacto (mesmo padrão do CustomerSummaryTab) */
function MetricCard({
  icon: Icon,
  label,
  value,
  subValue,
  variant = 'default',
}: {
  icon: typeof Briefcase;
  label: string;
  value: React.ReactNode;
  subValue?: string;
  variant?: 'default' | 'primary' | 'success' | 'warning';
}) {
  const variantClasses = {
    default: 'bg-muted/30',
    primary: 'bg-primary/5',
    success: 'bg-emerald-50 dark:bg-emerald-900/20',
    warning: 'bg-amber-50 dark:bg-amber-900/20',
  };

  return (
    <div className={cn('rounded-lg p-3 space-y-1', variantClasses[variant])}>
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-muted-foreground" />
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      </div>
      <div className="text-lg font-bold leading-none">{value}</div>
      {subValue && <span className="text-[10px] text-muted-foreground">{subValue}</span>}
    </div>
  );
}

/** Aba de resumo do veículo */
export function VehicleSummaryTab({
  vehicle,
  counts,
  isActive = true,
  sidebarContent,
}: VehicleSummaryTabProps) {
  return (
    <div className="space-y-4">
      {/* v2.1.0: ficha lateral no mobile (injetada pelo modal) */}
      {sidebarContent && <div className="-mx-1">{sidebarContent}</div>}

      {/* Grid de métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard icon={Briefcase} label="Negócios" value={counts?.deals ?? 0} />
        <MetricCard
          icon={FileSignature}
          label="Contratos"
          value={counts?.contracts ?? 0}
          variant="primary"
        />
        <MetricCard
          icon={ListChecks}
          label="Tarefas"
          value={counts?.tasks ?? 0}
          variant="success"
        />
        <MetricCard
          icon={ReceiptText}
          label="Multas em aberto"
          value={formatCurrency(vehicle.fines_total_open)}
          subValue={counts?.fines ? `${counts.fines} multa(s)` : undefined}
          variant="warning"
        />
      </div>

      {/* Andamentos — mesmo bloco do modal de tarefas */}
      <div>
        <div className="p-3">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Andamentos
            </h4>
          </div>
          <div className="pb-4">
            <VehicleActivityTimeline vehicleId={vehicle.id} isActive={isActive} />
          </div>
        </div>
      </div>
    </div>
  );
}
